import { useEffect, useState } from 'react';
import { ContentCard, ContentCardSkeleton } from '@/components/ContentCard';
import { Gamepad2, Search, TrendingUp, Clock, Star } from 'lucide-react';
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GamesPage({ setTheaterContent }) {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const fetchGames = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${API}/content/games?page=${page}&limit=24`);
        if (response.ok) {
          const data = await response.json();
          setGames(data.games);
          setTotalPages(data.pages);
        }
      } catch (error) {
        console.error('Failed to fetch games:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchGames();
  }, [page]);

  const handleContentClick = async (game) => {
    try {
      const response = await fetch(`${API}/content/${game.content_id}`);
      if (response.ok) {
        const data = await response.json();
        setTheaterContent(data);
      }
    } catch (error) {
      console.error('Failed to load game:', error);
    }
  };

  const filteredGames = games.filter(game => 
    game.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    game.author_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="games-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Gamepad2 size={32} className="text-[#cc0000]" />
          <div>
            <h1 className="text-2xl font-black text-white">GAMES</h1>
            <p className="text-xs text-[#666]">Flash games preserved with Ruffle</p>
          </div>
        </div>
        
        {/* Search */}
        <div className="relative w-full md:w-64">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#666]" />
          <Input
            placeholder="Search games..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 bg-[#0a0a0a] border-[#333] text-[#44ff44] placeholder:text-[#444] focus:border-[#cc0000]"
            data-testid="games-search"
          />
        </div>
      </div>

      {/* Sort options */}
      <div className="flex items-center gap-4 text-xs">
        <span className="text-[#666]">SORT BY:</span>
        <button className="flex items-center gap-1 text-[#ffcc00] hover:text-[#ffd700]">
          <TrendingUp size={12} />
          TOP RATED
        </button>
        <button className="flex items-center gap-1 text-[#999] hover:text-white">
          <Clock size={12} />
          NEWEST
        </button>
        <button className="flex items-center gap-1 text-[#999] hover:text-white">
          <Star size={12} />
          MOST VIEWS
        </button>
      </div>

      {/* Games grid */}
      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {Array(20).fill(0).map((_, i) => <ContentCardSkeleton key={i} />)}
        </div>
      ) : filteredGames.length > 0 ? (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4" data-testid="games-grid">
          {filteredGames.map((game) => (
            <ContentCard 
              key={game.content_id}
              content={game}
              onClick={() => handleContentClick(game)}
            />
          ))}
        </div>
      ) : (
        <div className="p-12 text-center border border-dashed border-[#333]">
          <Gamepad2 size={48} className="mx-auto mb-4 text-[#333]" />
          <p className="text-[#666]">
            {searchQuery ? 'No games found matching your search.' : 'No games available yet. Upload some!'}
          </p>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="btn-secondary text-xs disabled:opacity-50"
            data-testid="games-prev-page"
          >
            PREV
          </button>
          <span className="text-xs text-[#666] mono px-4">
            PAGE {page} / {totalPages}
          </span>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="btn-secondary text-xs disabled:opacity-50"
            data-testid="games-next-page"
          >
            NEXT
          </button>
        </div>
      )}
    </div>
  );
}
